import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, Chip, DataTable, type BadgeTone, type DataTableColumn } from '@zarya/ui';
import type { IconName } from '@zarya/theme';
import { dataHour } from '@app/format';
import { t } from '@app/i18n';
import type { components } from '@app/api/types';
import { useIdentityRelease, useIdentityReleases, type Release } from '../Identities.api';
import { TabState } from './TabState';
import s from '../Identities.module.css';

/**
 * Aba **Releases** de T5 (FR-041, FR-043, FR-054): cada release descoberta no
 * registry, a situação derivada da aprovação ligada e, ao abrir uma, a
 * declaração e a **procedência** — scan, SBOM e assinatura como a esteira as
 * afirmou (a Zarya não verifica: só mostra, e diz quando é emulado).
 */

type StatusRelease = Release['situation'];

/**
 * Rótulo da situação, do catálogo (`releasesTab.situation.<enum>`).
 *
 * A chave é o valor do enum do contrato, em snake_case — mesmo critério de
 * `error.*` e do estado da identidade: vocabulário do contrato não se traduz
 * para outra grafia só para virar chave. 29/08: `conhecida` é a tag anterior à
 * release aprovada, registrada **sem pendência**; a linha existe para o
 * histórico do agente, e ninguém precisa decidir nada sobre ela.
 */
function statusLabel(situacao: StatusRelease): string {
  return t(`identities.releasesTab.situation.${situacao}`);
}

const STATUS_TOM: Record<StatusRelease, BadgeTone> = {
  sem_declaracao: 'neutral',
  pendente: 'warning',
  aprovada: 'success',
  rejeitada: 'danger',
  auto: 'success',
  substituida: 'neutral',
  // Neutro como `substituida`: não cobra ação (`warning`) nem anuncia aprovação
  // (`success`) — é linha de histórico.
  conhecida: 'neutral',
};

/**
 * Rótulo da FONTE (P4) — de qual dos três adapters de ingestão veio o artefato.
 *
 * O valor gravado é o nome do adapter (`registry-poll` | `ci-push` | `manual`) e a
 * palavra sai do catálogo i18n: assim o rótulo aparece em qualquer idioma sem que
 * o dado mude de idioma junto. Chave desconhecida cai na própria chave, que é
 * feio de propósito — é bug de catálogo, e tem de doer na revisão.
 */
function sourceLabel(source: Release['source']): string {
  return t(`common.source.${source}`);
}

/**
 * Ícone por fonte: quem foi buscar, quem empurrou, quem digitou. Os três dizem a
 * mesma coisa do rótulo, para quem varre a lista sem ler.
 */
const SOURCE_ICON: Record<Release['source'], IconName> = {
  'registry-poll': 'search',
  'ci-push': 'bolt',
  manual: 'person',
};

/** Chip de um bloco de procedência: emulado, real ou não informado (FR-043). */
function BlockProvenance({
  rotulo,
  bloco,
}: {
  rotulo: string;
  bloco: components['schemas']['ProvenanceBloco'] | undefined;
}) {
  const naoInformado = t('identities.releasesTab.provenance.notInformed');
  if (!bloco) {
    return (
      <Chip>
        {rotulo}: <span className={s.vazio}>{naoInformado}</span>
      </Chip>
    );
  }
  const emulado = bloco.emulated;
  return (
    <Chip icon={emulado === true ? 'warning' : 'check'}>
      {rotulo}:{' '}
      {emulado === true
        ? t('identities.releasesTab.provenance.emulated')
        : emulado === false
          ? t('identities.releasesTab.provenance.real')
          : naoInformado}
    </Chip>
  );
}

export interface ReleasesTabProps {
  identityId: string;
  ativa: boolean;
}

export function ReleasesTab({ identityId, ativa }: ReleasesTabProps) {
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const releases = useIdentityReleases(identityId, ativa);
  const detalhe = useIdentityRelease(identityId, ativa ? selecionada : null);

  const itens = releases.data?.items ?? [];

  const columns: DataTableColumn<Release>[] = [
    {
      key: 'release',
      header: t('identities.releasesTab.column.release'),
      cell: (release) => (
        <span className={s.duasLinhas}>
          <span className={s.monoForte}>{release.tag ?? t('identities.releasesTab.noTag')}</span>
          <span className={s.sub}>{release.digest.slice(0, 26)}…</span>
        </span>
      ),
    },
    {
      key: 'situacao',
      header: t('identities.releasesTab.column.situation'),
      // O protótipo chamava esta coluna de "Motivo"; o contrato entrega a
      // situação derivada da aprovação ligada, que é o mesmo dado com nome
      // melhor — e é o que existe.
      tip: t('identities.releasesTab.column.situation.tip'),
      cell: (release) => (
        <Badge
          tone={STATUS_TOM[release.situation]}
          tip={
            release.situation === 'conhecida'
              ? t('identities.releasesTab.situation.conhecida.tip')
              : undefined
          }
        >
          {statusLabel(release.situation)}
        </Badge>
      ),
    },
    {
      // P4 — a fonte fica ao lado da situação, e não escondida no detalhe: é ela
      // que diz quanta garantia a declaração desta linha tem, e o aprovador
      // precisa disso ANTES de abrir a release.
      key: 'fonte',
      header: t('common.sourceLabel'),
      tip: t('common.sourceTip'),
      cell: (release) => (
        <Chip icon={SOURCE_ICON[release.source]}>{sourceLabel(release.source)}</Chip>
      ),
    },
    {
      key: 'quando',
      header: t('identities.releasesTab.column.discoveredAt'),
      cell: (release) => dataHour(release.discovered_at) ?? '—',
    },
    {
      key: 'contrato',
      header: t('identities.releasesTab.column.contract'),
      cell: (release) =>
        release.contract_version != null ? (
          <Chip icon="file-shield">v{release.contract_version}</Chip>
        ) : (
          <span className={s.vazio}>—</span>
        ),
    },
  ];

  return (
    <TabState
      carregando={releases.isLoading}
      erro={releases.isError ? releases.error : null}
      tituloErro={t('identities.releasesTab.error')}
      onRetry={() => void releases.refetch()}
      rotulo={t('identities.releasesTab.loading')}
    >
      <DataTable<Release>
        label={t('identities.releasesTab.table.label')}
        columns={columns}
        rows={itens}
        rowKey={(release) => release.id}
        onRowClick={(release) => setSelecionada(release.id)}
        emptyIcon="clock"
        emptyTitle={t('identities.releasesTab.empty.title')}
        emptyMessage={t('identities.releasesTab.empty.message')}
      />

      {selecionada ? (
        <Card
          title={t('identities.releasesTab.provenanceCard.title')}
          subtitle={t('identities.releasesTab.provenanceCard.subtitle')}
          className={s.cardEmpilhado}
        >
          <TabState
            carregando={detalhe.isLoading}
            erro={detalhe.isError ? detalhe.error : null}
            tituloErro={t('identities.releasesTab.provenance.error')}
            onRetry={() => void detalhe.refetch()}
            rotulo={t('identities.releasesTab.provenance.loading')}
          >
            {detalhe.data?.provenance?.available ? (
              <>
                <div className={s.capLista}>
                  <BlockProvenance rotulo={t('identities.releasesTab.provenance.scan')} bloco={detalhe.data.provenance.scan} />
                  <BlockProvenance rotulo={t('identities.releasesTab.provenance.sbom')} bloco={detalhe.data.provenance.sbom} />
                  <BlockProvenance
                    rotulo={t('identities.releasesTab.provenance.signature')}
                    bloco={detalhe.data.provenance.signature}
                  />
                </div>
                <dl className={s.leitura}>
                  {/*
                    P4 — a procedência é onde a fonte tem de aparecer, porque é aqui
                    que ela muda de significado: na varredura o rótulo viaja dentro
                    da imagem e o digest o fixa; na esteira e no manual a declaração
                    chega por fora, e aí «quem declarou» passa a ser um fato.
                  */}
                  <dt>{t('common.sourceLabel')}</dt>
                  <dd data-testid="release-fonte">
                    <Chip icon={SOURCE_ICON[detalhe.data.source]}>
                      {sourceLabel(detalhe.data.source)}
                    </Chip>
                  </dd>
                  {detalhe.data.provenance.declared_by ? (
                    <>
                      <dt>{t('common.declaredBy')}</dt>
                      <dd data-testid="release-declarada-por">
                        {detalhe.data.provenance.declared_by}
                      </dd>
                    </>
                  ) : null}
                  <dt>{t('identities.releasesTab.digest')}</dt>
                  <dd className={s.mono}>{detalhe.data.digest}</dd>
                  <dt>{t('identities.releasesTab.commit')}</dt>
                  <dd className={s.mono}>
                    {detalhe.data.provenance.commit ?? <span className={s.vazio}>—</span>}
                  </dd>
                  <dt>{t('identities.releasesTab.repository')}</dt>
                  <dd className={s.mono}>
                    {detalhe.data.provenance.repository ?? <span className={s.vazio}>—</span>}
                  </dd>
                  <dt>{t('identities.releasesTab.builtAt')}</dt>
                  <dd>{dataHour(detalhe.data.provenance.built_at) ?? '—'}</dd>
                  <dt>{t('identities.releasesTab.declaration')}</dt>
                  <dd>
                    {detalhe.data.declaration ? (
                      <span className={s.capLista}>
                        {Object.entries(detalhe.data.declaration.mcp ?? {}).flatMap(
                          ([resource, tools]) =>
                            // O contrato permite `string` ou `{ name, constraints }` em cada item
                            // (CapabilitiesDoc.mcp: items {}). Renderizar o objeto cru quebrava a tela
                            // (React #31) na primeira release com limite declarado — 28/08.
                            (tools as unknown[]).map((tool) => {
                              const nome =
                                typeof tool === 'string'
                                  ? tool
                                  : String((tool as { name?: unknown })?.name ?? '?');
                              const limits =
                                typeof tool === 'object' && tool !== null && 'constraints' in tool
                                  ? Object.entries(
                                      ((tool as { constraints?: Record<string, unknown> })
                                        .constraints ?? {}) as Record<string, unknown>,
                                    )
                                      .map(([k, v]) => `${k} ≤ ${String(v)}`)
                                      .join(' · ')
                                  : '';
                              return (
                                <Chip key={`${resource}.${nome}`}>
                                  {limits ? `${nome} (${limits})` : nome}
                                </Chip>
                              );
                            }),
                        )}
                        {(detalhe.data.declaration.a2a ?? []).map((slug) => (
                          <Chip key={slug} icon="exchange">
                            invoke_agent → {slug}
                          </Chip>
                        ))}
                      </span>
                    ) : (
                      <span className={s.vazio}>{t('identities.releasesTab.declaration.none')}</span>
                    )}
                  </dd>
                </dl>
                {detalhe.data.approval_id ? (
                  <Link className={s.link} to={`/aprovacoes?drawer=${detalhe.data.approval_id}`}>
                    {t('identities.releasesTab.seeApproval')}
                  </Link>
                ) : null}
              </>
            ) : (
              <p className={s.vazio}>
                {t('identities.releasesTab.provenance.unavailable')}
                {detalhe.data?.provenance?.reason ? ` (${detalhe.data.provenance.reason})` : ''}
              </p>
            )}
          </TabState>
        </Card>
      ) : null}
    </TabState>
  );
}
